import { useMutation } from "@tanstack/react-query";
import { saveAs } from "file-saver";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { fetchDataGetBlobFile } from "@/core/api/api-methods";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";

interface Args {
  batchId: string;
  lineId: string;
}

/**
 * Télécharge le relevé individuel PDF d'un salarié payé (`GET
 * /PayrollBatches/{id}/lines/{lineId}/statement`) : employeur, salarié, salaire
 * brut, frais, net reçu, date et référence du versement. Ne remplace pas le
 * bulletin de salaire. Codes : 200 `application/pdf` ; 404 ; 422 salarié non
 * payé.
 */
export const useDownloadPayrollLineStatement = () =>
  useMutation({
    mutationKey: ["payroll", "payrollBatches", "lineStatement"],
    mutationFn: async ({ batchId, lineId }: Args) => {
      const { blob, filename } = await fetchDataGetBlobFile({
        url: payrollEndpoints.payrollBatches.lineStatement(batchId, lineId),
        acceptHeader: "application/pdf",
      });
      if (blob.type.includes("json")) {
        const text = await blob.text();
        throw new Error(text || "Payroll line statement download failed");
      }
      saveAs(blob, filename ?? `releve-salarie-${lineId}.pdf`);
      return filename;
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useDownloadPayrollLineStatement failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
